import { el, tipHide } from "./dom";

// The modal (brief 1A-6, mockup modalShell): a title with a Close button, an optional line under
// it, the body and the actions, on a scrim. Esc, Close or a click on the scrim closes it. Focus
// moves into it, Tab and Shift+Tab stay inside, and on close it goes back where it was. At phone
// width it fills the screen as a sheet. The menu sheet and Ctrl K sit on the same layer. A modal
// with unsaved typing asks Save, Discard or Stay before Esc, Close or the scrim closes it (brief
// 1E-23, app/ui/unsaved.ts); that question sits on top of it.

/** Asks before a modal with unsaved typing is closed by the person (set by app/ui/unsaved.ts). */
export const leaving: { ask?: (root: HTMLElement) => Promise<boolean> } = {};

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (x) => !x.hidden && x.getClientRects().length > 0,
  );
}

/**
 * Opens content on the scrim as a dialog, keeping focus inside until it closes. Returns a close
 * function. Used by openModal, the phone menu sheet and Ctrl K.
 */
export function openDialog(
  content: HTMLElement,
  opts: {
    label: string;
    className?: string;
    top?: boolean;
    onClose?: () => void;
    /** Esc or a click on the scrim: what the person asked for (closes unless given). */
    dismiss?: () => void;
    /** On top of what's open, which stays (the Save your changes? question). */
    stack?: boolean;
  },
): () => void {
  const layer =
    document.getElementById("layer") ?? document.body.appendChild(el("div", { id: "layer" }));
  const modal = el(
    "div",
    {
      class: `modal ${opts.className ?? ""}`.trim(),
      role: "dialog",
      "aria-modal": "true",
      "aria-label": opts.label,
      tabindex: "-1",
    },
    content,
  );
  const scrim = el(
    "div",
    { class: "scrim", style: opts.top ? "align-items:flex-start;padding-top:10vh" : undefined },
    modal,
  );
  const before = document.activeElement as HTMLElement | null;
  let closed = false;

  // Only the dialog on top answers keys and keeps focus.
  const onTop = () => layer.lastElementChild === scrim;
  const dismiss = () => (opts.dismiss ? opts.dismiss() : close());
  const onKey = (e: KeyboardEvent) => {
    if (!onTop()) return;
    if (e.key === "Escape") {
      // An activity panel open inside the modal closes first (app/ui/activity.ts).
      if ((e.target as Element | null)?.closest?.(".actpanel.open")) return;
      e.preventDefault();
      dismiss();
      return;
    }
    if (e.key !== "Tab") return;
    const items = focusables(modal);
    if (items.length === 0) {
      e.preventDefault();
      modal.focus();
      return;
    }
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === modal || !modal.contains(at))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (at === last || !modal.contains(at))) {
      e.preventDefault();
      first.focus();
    }
  };
  // Focus that lands outside (a click on the page behind) comes back in.
  const onFocus = (e: FocusEvent) => {
    if (!closed && onTop() && !modal.contains(e.target as Node)) modal.focus();
  };

  function close() {
    if (closed) return;
    closed = true;
    document.removeEventListener("keydown", onKey, true);
    document.removeEventListener("focusin", onFocus, true);
    scrim.remove();
    tipHide();
    opts.onClose?.();
    if (before?.isConnected) before.focus();
  }
  scrim.addEventListener("mousedown", (e) => {
    if (e.target === scrim) dismiss();
  });
  document.addEventListener("keydown", onKey, true);
  document.addEventListener("focusin", onFocus, true);
  if (opts.stack) layer.append(scrim);
  else layer.replaceChildren(scrim);
  modal.focus();
  return close;
}

export interface ModalOptions {
  title: string;
  /** A short line under the title (mockup: muted, small). */
  sub?: string;
  body: Node[];
  actions?: Node[];
  wide?: boolean;
  /** Another class on the dialog (the file viewer is wider still). */
  className?: string;
  onClose?: () => void;
}

export interface Modal {
  close: () => void;
  /** The dialog's content, for screens that add to it. */
  root: HTMLElement;
  /** A note under the title for a refusal ("That change wasn't saved."), hidden until used. */
  note: HTMLElement;
}

let seq = 0;

/** A modal as in the mockup: title and Close, an optional line, the body, then the actions. */
export function openModal(opts: ModalOptions): Modal {
  const id = `mh${++seq}`;
  const closeBtn = el("button", { type: "button", class: "btn sm" }, "Close");
  const note = el("div", { class: "note", role: "alert", hidden: true });
  const root = el(
    "div",
    { class: "dlg" },
    el("div", { class: "cardhead", style: "margin:0" }, el("h2", { id }, opts.title), closeBtn),
    opts.sub ? el("p", { class: "muted small", style: "margin:0" }, opts.sub) : null,
    note,
    ...opts.body,
    opts.actions?.length ? el("div", { class: "actions" }, ...opts.actions) : null,
  );
  // Closed by the person (Close, Esc or the scrim): with unsaved typing it asks first.
  const dismiss = async () => {
    if (!leaving.ask || (await leaving.ask(root))) close();
  };
  const close = openDialog(root, {
    label: opts.title,
    className: `kit${opts.wide ? " wide" : ""}${opts.className ? ` ${opts.className}` : ""}`,
    onClose: opts.onClose,
    dismiss: () => void dismiss(),
  });
  const dialog = root.parentElement;
  dialog?.removeAttribute("aria-label");
  dialog?.setAttribute("aria-labelledby", id);
  closeBtn.addEventListener("click", () => void dismiss());
  return { close, root, note };
}
